<template>
  <div class="max-w-[31rem] border-t border-black/12 pt-5" data-private-access-sticky-suppress>
    <p class="text-[0.6rem] uppercase tracking-[0.34em] text-gray-600">
      {{ copy.founderLabel }}
    </p>
    <p class="mt-6 font-['Playfair_Display'] text-[2.35rem] leading-none text-black md:text-[2.85rem]">
      {{ openingPrice }} €
    </p>
    <p class="mt-6 text-[0.84rem] leading-6 text-gray-700">
      {{ copy.retailPrefix }}
      <span class="line-through decoration-gray-500">{{ retailPrice }} €</span>
    </p>

    <p class="mt-6 max-w-[25rem] text-[0.84rem] leading-6 text-gray-700">
      {{ copy.note }}
    </p>

    <NuxtLink
      :to="formAnchorPath"
      class="mt-6 inline-flex min-h-11 items-center justify-center rounded-full border border-black bg-black px-7 py-3 text-center text-[0.62rem] uppercase tracking-[0.24em] text-white transition hover:bg-white hover:text-black"
      @click.prevent="focusPrivateAccessForm"
    >
      {{ copy.cta }}
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import {
  PRIVATE_ACCESS_FOUNDING_PRICE,
  PRIVATE_ACCESS_PREORDER_PRICE,
  PRIVATE_ACCESS_RETAIL_PRICE,
  resolvePrivateAccessOpeningPrice
} from '~/utils/privateAccessPricing'

const props = withDefaults(defineProps<{
  color?: string
  localePrefix?: string
  productType?: string
  retailPrice?: number | null
}>(), {
  color: 'black',
  localePrefix: '',
  productType: 'ankle-boots',
  retailPrice: PRIVATE_ACCESS_RETAIL_PRICE
})

const campaignPrice = ref<number | null>(null)

const isFrench = computed(() => props.localePrefix === '/fr')
const openingPrice = computed(() => resolvePrivateAccessOpeningPrice(campaignPrice.value || PRIVATE_ACCESS_FOUNDING_PRICE))
const retailPrice = computed(() => resolvePrivateAccessOpeningPrice(props.retailPrice || PRIVATE_ACCESS_RETAIL_PRICE))
const formAnchorPath = computed(() => '#private-access')

function focusPrivateAccessForm() {
  if (!import.meta.client) return

  const field = document.getElementById('private-access-first-name') as HTMLInputElement | null
  const section = document.getElementById('private-access')

  if (section) {
    const header = document.querySelector('.hd') as HTMLElement | null
    const headerOffset = header && !header.classList.contains('hd--hidden')
      ? Math.round(header.getBoundingClientRect().height + 40)
      : 112
    const top = section.getBoundingClientRect().top + window.scrollY - headerOffset
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  } else {
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  if (field) {
    window.setTimeout(() => {
      field.focus({ preventScroll: true })
    }, 420)
  }

  if (window.location.hash !== '#private-access') {
    history.replaceState(null, '', '#private-access')
  }
}

const copy = computed(() => {
  if (isFrench.value) {
    return {
      cta: 'Ouvrir la précommande',
      founderLabel: 'Prix fondateur',
      label: 'Accès fondateur',
      note: `Premières paires dès ${PRIVATE_ACCESS_FOUNDING_PRICE}€, puis ${PRIVATE_ACCESS_PREORDER_PRICE}€ selon le palier disponible. Aucun paiement maintenant.`,
      retailPrefix: 'Prix boutique '
    }
  }

  return {
    cta: 'Open preorder',
    founderLabel: 'Founder price',
    label: 'Founding access',
    note: `First pairs from ${PRIVATE_ACCESS_FOUNDING_PRICE}€, then ${PRIVATE_ACCESS_PREORDER_PRICE}€ depending on the available tier. No payment now.`,
    retailPrefix: 'Retail price '
  }
})

onMounted(async () => {
  try {
    const supabase = useSupabaseClient()
    const { data, error } = await supabase
      .from('preorder_campaigns')
      .select('preorder_price, end_date')
      .eq('status', 'active')
      .eq('product_type', props.productType)
      .eq('color', props.color)
      .order('end_date', { ascending: true })
      .limit(1)

    if (error) return

    const activeCampaign = (data || []).find((entry) => {
      if (!entry.end_date) return true
      return new Date(entry.end_date).getTime() > Date.now()
    })

    const price = Number(activeCampaign?.preorder_price)
    campaignPrice.value = Number.isFinite(price) && price > 0 ? price : null
  } catch {
    campaignPrice.value = null
  }
})
</script>
